/** @jsxImportSource react */
import { setupPostMessageHandlers } from '@common/helpers/MessageHandlers';
import {
  GwizzyCommunity,
  transformGwizzyCommunityFromCommunityModel,
} from '@common/helpers/transformGwizzyCommunity';
import { getPricingRequestConfirmationMessage } from '@react/chat/src/utils/helpers';
import useCta from '@react/cta/hooks/useCta';
import { normalizeResponse, useQuery } from '@react/services/api';
import { eventNames, useEvents } from '@react/services/events';
import { useToast } from '@react/shared/components/Toast';
import { delay } from '@react/shared/helpers/delay';
import {
  BotMessageRoleType,
  ChatBotProviderBotProps,
} from '@react/shared/types/bot';
import {
  APPLY_FILTERS,
  FALLBACK,
  FIND_COMMUNITY_IN_AREA,
  GET_PRICING_CTA,
  GUIDED_FORMS,
  MOUNT_BOT,
  OPEN_BOT,
  RECOMMENDATION_REQUEST_PRICING,
  REQUEST_PRICING,
  REQUEST_TOUR,
  REQUEST_TOUR_FROM_RECOMMENDATIONS,
} from '@react/virtual-advisor/constants';
import ChatbotContext from '@react/virtual-advisor/context/ChatbotContext';
import useChatState, {
  CHATBOT_ACTIONS,
} from '@react/virtual-advisor/hooks/useChatState';
import { GET_COMMUNITY_INCLUDE_PARAMS } from '@sly/core/constants/communities';
import { Community, GPTBotMode } from '@sly/core/types';
import { nanoid } from 'ai';
import { useChat } from 'ai/react';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { formatMessages, getSearchQueryParamsFromChat } from '../helpers/utils';

const getInitialMessage = (chatbotMode: GPTBotMode) => {
  switch (chatbotMode) {
    case 'community':
      return ' I can answer any question about this community.';
    case 'search':
      return ' I can help you find the right community.';
    default:
      return ' I can help you find the right community.';
  }
};

const ChatbotProvider = ({
  children,
  gwizzyCommunity,
  query,
  onApplySearchFilters,
  onPush,
  botProps,
  chatbotMode = 'default',
  pathname,
}: {
  children: ReactNode;
  gwizzyCommunity: GwizzyCommunity | null;
  query: any;
  botProps?: ChatBotProviderBotProps;
  onApplySearchFilters?: (query: { [key: string]: string | number }) => void;
  onPush?: (url: string) => void;
  chatbotMode: GPTBotMode;
  pathname: string;
}) => {
  const chatReducer = useChatState();
  const { events } = useEvents();
  const { dispatch, state } = chatReducer;
  const { isInitialized } = state;
  const { pushSuccess } = useToast();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { pushError } = useToast();
  const getCommunity = useQuery('getCommunityNew');
  const getSearchCommunity = useQuery('getSearchCommunity');

  const defaultEventProps = useMemo(
    () => ({
      pathname,
      chatbotMode,
      location: 'AI virtual advisor chat',
    }),
    [pathname, chatbotMode]
  );

  const {
    pricingRequested: completedPricing,
    completedAssessment,
    getPricingRequestedListBySlugs,
    createProfileContactActionBySlugs,
    completedAssessmentFlow,
    getPricingRequestedBySlug,
    createProfileContactActionBySlug,
  } = useCta({
    community: gwizzyCommunity,
    mode: {},
    isOpenByDefault: false,
    params: query,
    isChatMode: true,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
    // inputRef?.current?.focus?.();
  }, [messagesEndRef]);

  const slug = query?.slug;

  const initialMessage = getInitialMessage(chatbotMode);

  const initialMessages = formatMessages(
    '👋 Hi there, I’m Seniorly’s virtual advisor.' + initialMessage,
    [],
    'assistant'
  );

  let budget = '';
  const { startingRate } = gwizzyCommunity || {};
  if (startingRate) {
    if (startingRate < 3000) {
      budget = 'low';
    } else if (startingRate < 5000) {
      budget = 'medium';
    } else if (startingRate < 10000) {
      budget = 'high';
    }
  }

  const chatAI = useChat({
    initialMessages: localStorage.getItem('chat')
      ? JSON.parse(localStorage.getItem('chat') || '[]')
      : initialMessages,
    body: {
      communitySlug: slug,
      query,
      chatbotMode,
      budget,
    },
    onResponse(response) {
      if (response.status === 401) {
        pushError(response.statusText);
      }
    },
    async experimental_onFunctionCall(chatMessages, functionCall) {
      const { name = '', arguments: args } = functionCall || {};

      events.track(eventNames.ChatbotFunctionInvoked, {
        ...defaultEventProps,
        text: name,
      });

      switch (name) {
        case REQUEST_PRICING: {
          const { communitySlug } = JSON.parse(args || '{}');
          const { startingRate, name: communityName } = gwizzyCommunity || {};
          const isProccessed = await handleSingleCommunityRequestPricing({
            communityName,
            communitySlug,
            startingRate,
            functionName: name,
          });
          if (isProccessed) {
            break;
          }
          dispatch({
            type: CHATBOT_ACTIONS.SET_GUIDED_FLOW,
            payload: REQUEST_PRICING,
          });
          break;
        }
        case RECOMMENDATION_REQUEST_PRICING:
          try {
            const { communitySlugs } = JSON.parse(args || '{}');

            if (!communitySlugs?.length) {
              await append({
                role: 'function',
                name: RECOMMENDATION_REQUEST_PRICING,
                content: 'No communities found',
                id: nanoid(),
              });
              break;
            }

            const resp = await getSearchCommunity({
              'filter[id]': communitySlugs,
              include: GET_COMMUNITY_INCLUDE_PARAMS,
              exp: 'or',
            });

            const communities = normalizeResponse(resp?.body) as Community[];

            if (!communities?.length) {
              await append({
                role: 'function',
                name: RECOMMENDATION_REQUEST_PRICING,
                content: 'No communities found',
                id: nanoid(),
              });
              break;
            }

            const recommendedCommunities: GwizzyCommunity[] = communities
              ?.map(
                (community) =>
                  transformGwizzyCommunityFromCommunityModel(
                    community,
                    false,
                    null
                  ) as GwizzyCommunity
              )
              .filter((community) => !!community);
            if (recommendedCommunities?.length === 1) {
              const {
                id: communitySlug,
                name: communityName,
                startingRate,
              } = recommendedCommunities[0];
              const isProccessed = await handleSingleCommunityRequestPricing({
                communitySlug,
                communityName,
                startingRate,
                functionName: name,
              });
              if (isProccessed) {
                break;
              }
            }
            dispatch({
              type: CHATBOT_ACTIONS.SET_RECOMMENDED_COMMUNITIES,
              payload: recommendedCommunities,
            });
            dispatch({
              type: CHATBOT_ACTIONS.SET_GUIDED_FLOW,
              payload: RECOMMENDATION_REQUEST_PRICING,
            });
            break;
          } catch (err) {
            console.error(err);
          }
          break;

        case REQUEST_TOUR: {
          const { communitySlug } = JSON.parse(args || '{}');
          if (communitySlug != gwizzyCommunity?.id) {
            const resp = await getCommunity({
              id: communitySlug,
              include: GET_COMMUNITY_INCLUDE_PARAMS,
            });
            const deserializedResponse = normalizeResponse(
              resp?.body
            ) as Community;
            const community = transformGwizzyCommunityFromCommunityModel(
              deserializedResponse,
              false,
              null
            );
            if (community) {
              dispatch({
                type: CHATBOT_ACTIONS.SET_GUIDED_FLOW,
                payload: REQUEST_TOUR_FROM_RECOMMENDATIONS,
              });
              dispatch({
                type: CHATBOT_ACTIONS.SET_USER_OPTED_PRIMARY_COMMUNITY,
                payload: community,
              });
              break;
            }
          }
          dispatch({ type: CHATBOT_ACTIONS.SET_GUIDED_FLOW, payload: name });
          break;
        }

        case FALLBACK:
          dispatch({ type: CHATBOT_ACTIONS.SET_GUIDED_FLOW, payload: name });
          break;

        case APPLY_FILTERS: {
          const filters = JSON.parse(args || '{}');
          const searchQuery = {
            ...getSearchQueryParamsFromChat({ ...filters, ...(query || {}) }),
          };

          if (onApplySearchFilters) {
            onApplySearchFilters(searchQuery);
            await append({
              role: 'function',
              name: APPLY_FILTERS,
              content: JSON.stringify({
                message: 'Applying filters',
                ...searchQuery,
              }),
              id: nanoid(),
            });
            pushSuccess('Filters applied successfully');
          }

          await delay(1000);

          break;
        }

        case FIND_COMMUNITY_IN_AREA: {
          dispatch({ type: CHATBOT_ACTIONS.SET_GUIDED_FLOW, payload: name });
          break;
        }

        default:
          break;
      }
    },
  });
  const { append } = chatAI;

  async function handleSingleCommunityRequestPricing({
    communitySlug,
    communityName,
    startingRate,
    functionName,
  }: {
    communitySlug?: string;
    communityName?: string;
    startingRate?: number | null;
    functionName: string;
  }) {
    const isPricingRequested = await getPricingRequestedBySlug(communitySlug);
    // If user already requested pricing for that community, show confirmation message
    if (isPricingRequested) {
      const confirmationMessage =
        getPricingRequestConfirmationMessage(gwizzyCommunity);

      await append({
        role: 'function',
        name: functionName,
        content: JSON.stringify({
          confirmationMessage,
          communityName: communityName,
          startingRate,
        }),
        id: nanoid(),
      });
      return true;
    }
    // If user completed assement flow then create uuidAction and show confirmation message
    if (completedAssessment) {
      try {
        await createProfileContactActionBySlug(communitySlug);
      } catch (err) {
        console.error(err);
        await append({
          role: 'function',
          name: REQUEST_PRICING,
          content: 'Unable to procceed with the request',
          id: nanoid(),
        });
        return true;
      }
      await completedAssessmentFlow();
      const confirmationMessage =
        getPricingRequestConfirmationMessage(gwizzyCommunity);
      await append({
        role: 'function',
        name: functionName,
        content: JSON.stringify({
          confirmationMessage,
          communityName: communityName,
          startingRate,
        }),
        id: nanoid(),
      });
      return true;
    }
  }

  async function handleStartGuidedFlow({ name }: { name: string }) {
    if (!GUIDED_FORMS.includes(name as (typeof GUIDED_FORMS)[number])) {
      return;
    }
    dispatch({ type: 'SET_GUIDED_FLOW', payload: name });
  }

  const onTourRequestSubmit = async (values: Record<string, any>) => {
    dispatch({ type: 'SET_GUIDED_FLOW', payload: '' });
    await append({
      role: 'function',
      name: REQUEST_TOUR,
      content: JSON.stringify(values),
      id: nanoid(),
    });
  };

  const onGwizzySubmit = async (state: any) => {
    const { referredAgent, referredCommunities, conversionData } = state;
    let data: {
      conversionData: any;
      status: string;
      referredCommunities?: string[];
      referredAgent?: string;
    } = {
      conversionData,
      status: 'Pricing request sent',
    };

    if (referredCommunities?.length) {
      data = {
        ...data,
        referredCommunities: referredCommunities.map(
          ({ name }: { name: string }) => name
        ),
      };
    } else if (referredAgent) {
      data = {
        ...data,
        referredAgent: referredAgent?.info?.displayName,
      };
    }

    dispatch({ type: CHATBOT_ACTIONS.SET_GUIDED_FLOW, payload: '' });
    dispatch({ type: CHATBOT_ACTIONS.SET_IS_SHOWING_NUDGES, payload: true });
    await append({
      role: 'function',
      name: REQUEST_PRICING,
      content: JSON.stringify(data),
      id: nanoid(),
    });
  };

  const onAskQuestionSubmit = async (values: Record<string, any>) => {
    dispatch({ type: CHATBOT_ACTIONS.SET_GUIDED_FLOW, payload: '' });
    dispatch({ type: CHATBOT_ACTIONS.SET_IS_SHOWING_NUDGES, payload: true });
    await append({
      role: 'function',
      name: FALLBACK,
      content: JSON.stringify(values),
      id: nanoid(),
    });
  };

  const onFindCommunityInAreaSubmit = async (url: string) => {
    dispatch({ type: CHATBOT_ACTIONS.SET_GUIDED_FLOW, payload: '' });
    dispatch({ type: CHATBOT_ACTIONS.SET_IS_SHOWING_NUDGES, payload: true });
    if (onPush) {
      onPush(url);
      await append({
        role: 'function',
        name: FIND_COMMUNITY_IN_AREA,
        content: JSON.stringify({
          content: 'Here are some communities in your area.',
          url,
        }),
        id: nanoid(),
      });
      dispatch({ type: CHATBOT_ACTIONS.SET_CHAT_CLOSE });
    }
  };

  const openBot = useCallback(() => {
    dispatch({ type: CHATBOT_ACTIONS.SET_CHAT_OPEN });
    dispatch({
      type: CHATBOT_ACTIONS.SET_IS_SHOWING_NUDGES,
      payload: true,
    });
  }, [dispatch]);

  const mountBot = useCallback(() => {
    dispatch({ type: CHATBOT_ACTIONS.SET_CHAT_CLOSE });
  }, [dispatch]);

  const onCtaClick = useCallback(() => {
    dispatch({ type: CHATBOT_ACTIONS.SET_CHAT_OPEN });
    dispatch({
      type: CHATBOT_ACTIONS.SET_GUIDED_FLOW,
      payload: 'request_pricing',
    });
    dispatch({
      type: CHATBOT_ACTIONS.SET_IS_SHOWING_NUDGES,
      payload: true,
    });
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialized) {
      events.track(eventNames.ChatBotInitiated, {
        ...defaultEventProps,
      });
      dispatch({ type: CHATBOT_ACTIONS.SET_IS_INITIALIZED, payload: true });
    }
    const { openBotWithType, appendMessage } = botProps || {};
    if (openBotWithType) {
      handleEvent({ event: openBotWithType, appendMessage: appendMessage });
    }
  }, []);

  const handleEvent = useCallback(
    async ({
      event,
      appendMessage,
    }: {
      event: string;
      appendMessage?: { message?: string; role?: BotMessageRoleType };
    }) => {
      switch (event) {
        case GET_PRICING_CTA:
          onCtaClick();
          break;
        case OPEN_BOT:
          if (!state.isChatOpen) {
            openBot();
          }
          break;
        case MOUNT_BOT:
          mountBot();
          break;
        default:
          break;
      }

      // Don't append if bot is open
      if (!state.isChatOpen) {
        const { message, role } = appendMessage || {};
        if (message && role) {
          await append({
            role: role,
            content: message,
            id: nanoid(),
          });
        }
      }
    },
    [append, mountBot, onCtaClick, openBot, state.isChatOpen]
  );

  const postMessageHandlers = useCallback(
    async (msg: any) => {
      const { event, payload = {} } = msg.data;
      handleEvent({
        event,
        appendMessage: payload?.appendMessage,
      });
    },
    [handleEvent]
  );

  useEffect(() => {
    const unsubscribePostMessage = setupPostMessageHandlers(
      window,
      postMessageHandlers
    );
    return () => {
      unsubscribePostMessage();
    };
  }, [postMessageHandlers]);

  return (
    <ChatbotContext.Provider
      value={{
        chatReducer,
        chatAI,
        messagesEndRef,
        scrollToBottom,
        gwizzyCommunity,
        handleStartGuidedFlow,
        getPricingRequestedListBySlugs,
        getPricingRequestedBySlug,
        createProfileContactActionBySlugs,
        createProfileContactActionBySlug,
        onTourRequestSubmit,
        onGwizzySubmit,
        onAskQuestionSubmit,
        onFindCommunityInAreaSubmit,
        chatbotMode,
        inputRef,
        query,
        defaultEventProps,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};

export default ChatbotProvider;
